import { getVolume } from '../utils/volume';
/**
 * Browser TTS using Web Speech API
 * Free tier - instant playback, with word highlighting via boundary events
 *
 * Includes Chrome workarounds:
 * - pause()/resume() every 10s to prevent Chrome's ~15s cutoff bug
 * - Watchdog timer to force-resolve if onend never fires
 * - Manual _speaking flag (synth.speaking can be stale on Chrome)
 *
 * Word highlighting strategy:
 * 1. Native onboundary events (works with macOS system voices, NOT Google network voices)
 * 2. Timer-based fallback that starts immediately and cancels if native events fire
 */
import { debugLog } from '../utils/logger';

export class BrowserTTS {
  private synth: SpeechSynthesis;
  private utterance: SpeechSynthesisUtterance | null = null;
  private isPaused: boolean = false;
  private _speaking: boolean = false;  // manual flag — don't trust synth.speaking
  private onEndCallback: (() => void) | null = null;
  private onBoundaryCallback: ((word: string, charIndex: number, charLength: number, fullText: string) => void) | null = null;
  private chromeResumeTimer: ReturnType<typeof setInterval> | null = null;
  private watchdogTimer: ReturnType<typeof setTimeout> | null = null;
  private wordFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private nativeBoundaryFired: boolean = false;
  /** Bumped by stop() so a speak() still selecting its voice doesn't start */
  private requestId: number = 0;

  // Word fallback state — shared so sentence boundary sync can jump the position
  private fallbackWords: { word: string; charIndex: number }[] = [];
  private fallbackIndex: number = 0;
  private fallbackEmitNext: (() => void) | null = null;

  constructor() {
    if (!('speechSynthesis' in window)) {
      throw new Error('Browser TTS not supported');
    }

    this.synth = window.speechSynthesis;
  }

  /**
   * Check if browser TTS is available
   */
  static isSupported(): boolean {
    return 'speechSynthesis' in window;
  }

  /**
   * Get available voices
   */
  async getVoices(): Promise<SpeechSynthesisVoice[]> {
    return new Promise((resolve) => {
      let voices = this.synth.getVoices();

      if (voices.length > 0) {
        resolve(voices);
      } else {
        // Voices load async on some browsers
        this.synth.addEventListener('voiceschanged', () => {
          voices = this.synth.getVoices();
          resolve(voices);
        }, { once: true });
      }
    });
  }

  /**
   * Voice ID → browser voice search strategy
   * Maps our voice IDs to browser voice preferences.
   *
   * Google Chrome voices (e.g. "Google US English") sound best.
   * Native OS voices (macOS/Windows) fire onboundary events for precise highlighting.
   * Timer fallback handles word highlighting for Google voices.
   */
  private static VOICE_MAP: Record<string, { keywords: string[]; gender: 'male' | 'female' }> = {
    heart:   { keywords: ['Samantha', 'Google US English', 'Microsoft Aria', 'Karen'],  gender: 'female' },
    michael: { keywords: ['Alex', 'Google US English', 'Microsoft David', 'Daniel'],    gender: 'male' },
    lily:    { keywords: ['Google US English', 'Samantha', 'Microsoft Aria', 'Karen'],  gender: 'female' },
    leo:     { keywords: ['Fred', 'Microsoft Mark', 'Tom', 'Google US English'],        gender: 'male' },
    mia:     { keywords: ['Google UK English Female', 'Karen', 'Microsoft Aria', 'Tessa'], gender: 'female' },
    theo:    { keywords: ['Google UK English Male', 'Daniel', 'Microsoft George', 'Oliver'], gender: 'male' },
    alloy:   { keywords: ['Google US English', 'Samantha', 'Microsoft Aria', 'Karen'],  gender: 'female' },
    echo:    { keywords: ['Fred', 'Microsoft Mark', 'Tom', 'Google US English'],        gender: 'male' },
    onyx:    { keywords: ['Google UK English Male', 'Daniel', 'Microsoft George', 'Oliver'], gender: 'male' },
    fable:   { keywords: ['Google UK English Male', 'Daniel', 'Microsoft George', 'Oliver'], gender: 'male' },
    nova:    { keywords: ['Google UK English Female', 'Karen', 'Microsoft Aria', 'Tessa'], gender: 'female' },
    shimmer: { keywords: ['Moira', 'Tessa', 'Microsoft Jenny', 'Google US English'],   gender: 'female' },
  };

  private static FEMALE_HINTS = ['female', 'woman', 'zira', 'jenny', 'aria', 'samantha', 'karen', 'victoria', 'moira', 'tessa', 'fiona'];
  private static MALE_HINTS = ['male', 'man', 'david', 'mark', 'daniel', 'george', 'alex', 'tom', 'fred', 'oliver'];

  /**
   * Select best voice for given name/ID
   * Tries keyword matches first (best quality), then gender, then any English voice.
   */
  async selectVoice(voiceName: string): Promise<SpeechSynthesisVoice | null> {
    const voices = await this.getVoices();
    const englishVoices = voices.filter(v => v.lang.startsWith('en'));

    const mapping = BrowserTTS.VOICE_MAP[voiceName];

    if (mapping) {
      // 1. Try keyword matches in order of preference
      for (const keyword of mapping.keywords) {
        const match = englishVoices.find(v =>
          v.name.toLowerCase().includes(keyword.toLowerCase())
        );
        if (match) {
          debugLog('[RV] Voice selected:', match.name, 'for', voiceName);
          return match;
        }
      }

      // 2. Try gender-based matching
      const genderHints = mapping.gender === 'female' ? BrowserTTS.FEMALE_HINTS : BrowserTTS.MALE_HINTS;
      const genderMatch = englishVoices.find(v => {
        const name = v.name.toLowerCase();
        return genderHints.some(hint => name.includes(hint));
      });
      if (genderMatch) {
        debugLog('[RV] Voice selected (gender):', genderMatch.name, 'for', voiceName);
        return genderMatch;
      }
    }

    // 5. Try direct name match
    let voice = englishVoices.find(v =>
      v.name.toLowerCase().includes(voiceName.toLowerCase())
    );
    if (voice) return voice;

    // 6. Default English voice
    voice = englishVoices.find(v => v.default);
    if (voice) return voice;

    // 7. Any English voice
    if (englishVoices.length > 0) return englishVoices[0];

    // 8. Last resort
    return voices.length > 0 ? voices[0] : null;
  }

  /**
   * Speak text
   */
  async speak(
    text: string,
    voiceName: string = 'alloy',
    speed: number = 1.0
  ): Promise<void> {
    // Cancel any ongoing speech
    this.stop();
    const requestId = this.requestId;

    // Select voice before creating the promise
    const voice = await this.selectVoice(voiceName);

    // stop() (or another speak()) was called while voices were loading
    if (requestId !== this.requestId) return;

    debugLog('[RV] speak() called — voice:', voice?.name, 'speed:', speed, 'text length:', text.length);

    return new Promise<void>((resolve) => {
      let resolved = false;
      const utterance = new SpeechSynthesisUtterance(text);
      const done = () => {
        if (resolved) return;
        resolved = true;
        // A cancelled utterance can report its end after the next speak() has
        // started (rapid sentence-by-sentence playback) — don't clobber that one
        if (this.utterance === utterance || this.utterance === null) {
          this._speaking = false;
          this.clearTimers();
          if (this.onEndCallback) this.onEndCallback();
        }
        resolve();
      };

      this.utterance = utterance;

      if (voice) {
        this.utterance.voice = voice;
      }

      this.utterance.rate = Math.max(0.5, Math.min(2.0, speed));
      this.utterance.pitch = 1.0;
      this.utterance.volume = getVolume();

      this.utterance.onend = () => {
        debugLog('[RV] utterance.onend fired');
        done();
      };

      this.utterance.onerror = (event) => {
        debugLog('[RV] utterance.onerror:', event.error);
        done();
      };

      // Boundary events — word and sentence
      this.nativeBoundaryFired = false;
      this.utterance.onboundary = (event) => {
        if (event.name === 'word' && this.onBoundaryCallback) {
          if (!this.nativeBoundaryFired) {
            this.nativeBoundaryFired = true;
            // Native word boundary works — kill the fallback timer
            debugLog('[RV] Native word boundary firing — cancelling timer fallback');
            if (this.wordFallbackTimer) {
              clearTimeout(this.wordFallbackTimer);
              this.wordFallbackTimer = null;
            }
          }
          const charIndex = event.charIndex;
          const charLen = (event as any).charLength || 0;
          const word = charLen > 0
            ? text.substring(charIndex, charIndex + charLen)
            : text.substring(charIndex).split(/\s/)[0];
          this.onBoundaryCallback(word, charIndex, charLen || word.length, text);
        }

        // Sentence boundary — sync the fallback timer to the correct position.
        // Google voices often fire sentence boundaries even when they skip word boundaries.
        if (event.name === 'sentence' && !this.nativeBoundaryFired && this.fallbackWords.length > 0) {
          const sentenceCharIndex = event.charIndex;
          debugLog('[RV] Sentence boundary at char', sentenceCharIndex, '— syncing fallback timer');

          // Find the word closest to this sentence boundary
          let targetIdx = this.fallbackIndex;
          for (let j = 0; j < this.fallbackWords.length; j++) {
            if (this.fallbackWords[j].charIndex >= sentenceCharIndex) {
              targetIdx = j;
              break;
            }
          }

          // Jump the fallback timer to this position if it's ahead of where we are
          if (targetIdx > this.fallbackIndex) {
            this.fallbackIndex = targetIdx;
            // Cancel current scheduled word and emit the correct one now
            if (this.wordFallbackTimer) {
              clearTimeout(this.wordFallbackTimer);
              this.wordFallbackTimer = null;
            }
            if (this.fallbackEmitNext) {
              this.fallbackEmitNext();
            }
          }
        }
      };

      // Chrome workaround: pause/resume every 10s to prevent the ~15s cutoff bug
      this.chromeResumeTimer = setInterval(() => {
        if (this.synth.speaking && !this.synth.paused) {
          this.synth.pause();
          this.synth.resume();
        }
      }, 10000);

      // Watchdog: force-resolve if onend never fires (Chrome bug)
      const estimatedMs = (text.split(/\s+/).length / 2.5 / Math.max(0.5, speed)) * 1000;
      const watchdogMs = Math.max(estimatedMs * 3, 10000);
      this.watchdogTimer = setTimeout(() => {
        if (!resolved) {
          debugLog('[RV] Watchdog fired — forcing speech end after', watchdogMs, 'ms');
          this.synth.cancel();
          done();
        }
      }, watchdogMs);

      // Start speaking
      this.synth.speak(this.utterance);
      this._speaking = true;
      this.isPaused = false;

      // Start word timer fallback IMMEDIATELY — if native onboundary fires,
      // the timer gets cancelled. This avoids the old 500ms detection delay.
      if (this.onBoundaryCallback) {
        this.startWordFallback(text, speed);
      }
    });
  }

  /**
   * Clear Chrome workaround timers
   */
  private clearTimers(): void {
    if (this.chromeResumeTimer) { clearInterval(this.chromeResumeTimer); this.chromeResumeTimer = null; }
    if (this.watchdogTimer) { clearTimeout(this.watchdogTimer); this.watchdogTimer = null; }
    if (this.wordFallbackTimer) { clearTimeout(this.wordFallbackTimer); this.wordFallbackTimer = null; }
    this.fallbackEmitNext = null;
    this.fallbackWords = [];
    this.fallbackIndex = 0;
  }

  /**
   * Timer-based word highlighting fallback for voices without onboundary support.
   * Starts immediately. Cancelled if native word boundary fires.
   * Sentence boundary events sync the position to prevent drift.
   * Estimates word timing from average speaking rate (~150 wpm at 1x).
   */
  private startWordFallback(text: string, speed: number): void {
    if (!this.onBoundaryCallback) return;

    // Split text into words with their char positions
    const wordPattern = /\S+/g;
    this.fallbackWords = [];
    let match: RegExpExecArray | null;
    while ((match = wordPattern.exec(text)) !== null) {
      this.fallbackWords.push({ word: match[0], charIndex: match.index });
    }

    if (this.fallbackWords.length === 0) return;

    // ~150 words/min = ~400ms per word at 1x speed
    const baseMs = 400 / Math.max(0.5, speed);
    this.fallbackIndex = 0;

    const emitNext = () => {
      // Stop if native word boundary took over, speech ended, or paused
      if (this.nativeBoundaryFired || this.fallbackIndex >= this.fallbackWords.length || !this._speaking || this.isPaused) return;

      const { word, charIndex } = this.fallbackWords[this.fallbackIndex];
      this.onBoundaryCallback!(word, charIndex, word.length, text);
      this.fallbackIndex++;

      if (this.fallbackIndex < this.fallbackWords.length && this._speaking && !this.nativeBoundaryFired) {
        // Vary timing by word length: short words ~70% base, long words ~130% base
        const factor = 0.7 + Math.min(word.length / 12, 0.6);
        this.wordFallbackTimer = setTimeout(emitNext, baseMs * factor);
      }
    };

    // Store emitNext so sentence boundary sync can call it
    this.fallbackEmitNext = emitNext;

    // Emit first word immediately
    emitNext();
  }

  /**
   * Pause speech
   */
  pause(): void {
    if (this._speaking && !this.isPaused) {
      this.synth.pause();
      this.isPaused = true;
      // Pause fallback timer
      if (this.wordFallbackTimer) {
        clearTimeout(this.wordFallbackTimer);
        this.wordFallbackTimer = null;
      }
    }
  }

  /**
   * Resume speech
   */
  resume(): void {
    if (this.isPaused) {
      this.synth.resume();
      this.isPaused = false;

      // Restart fallback timer if it was active
      if (!this.nativeBoundaryFired && this.fallbackEmitNext &&
          this.fallbackIndex < this.fallbackWords.length) {
        this.fallbackEmitNext();
      }
    }
  }

  /**
   * Stop speech
   */
  stop(): void {
    this.requestId++;
    this.synth.cancel();
    this._speaking = false;
    this.isPaused = false;
    this.utterance = null;
    this.clearTimers();
  }

  /**
   * Check if speaking — uses manual flag, NOT synth.speaking (stale on Chrome)
   */
  isSpeaking(): boolean {
    return this._speaking;
  }

  /**
   * Check if paused
   */
  isPausedState(): boolean {
    return this.isPaused;
  }

  /**
   * Set end callback
   */
  onEnd(callback: () => void): void {
    this.onEndCallback = callback;
  }

  /**
   * Set word boundary callback — fires on each word during speech
   * @param callback (word, charIndex, charLength, fullText)
   */
  onBoundary(callback: (word: string, charIndex: number, charLength: number, fullText: string) => void): void {
    this.onBoundaryCallback = callback;
  }

  /**
   * Estimate duration (rough approximation)
   * Browser TTS doesn't provide actual duration
   */
  estimateDuration(text: string, speed: number = 1.0): number {
    // Average speaking rate: 150 words/minute = 2.5 words/second
    const wordCount = text.split(/\s+/).length;
    const baseDuration = wordCount / 2.5;
    return baseDuration / speed;
  }
}
